/**
 * Centralized open/closed state for the Properties Panel's five sections.
 *
 * Object, Type, Photo, Layout, Effects are always rendered, in this order, and any number of
 * them may be open at once (Radix `Accordion type="multiple"`). Object/Layout/Effects persist
 * their open/closed state for the life of the panel. Type/Photo are tracked in a separate slice
 * so a later per-selection/per-object memory model can key their state without touching the
 * persistent trio or any consumer of this hook.
 *
 * Also tracks which of Layout/Effects — if either — is currently "pinned": opened more recently
 * than the other, while still open. `usePropertiesPanelScroll` scrolls to it on a selection change
 * when the newly-selected object's own section is closed (see `resolvePropertiesPanelScrollTarget`).
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { PROPERTIES_PANEL_SECTION_ORDER } from '@/lib/panels/constants/properties-panel-section-order'
import type { PropertiesPanelSectionId } from '@/lib/panels/types/properties-panel-section-id'

const PERSISTENT_SECTION_IDS = ['object', 'layout', 'effects'] as const

type PersistentSectionId = (typeof PERSISTENT_SECTION_IDS)[number]

const SELECTION_SCOPED_SECTION_IDS = ['type', 'photo'] as const

type SelectionScopedSectionId = (typeof SELECTION_SCOPED_SECTION_IDS)[number]

const DEFAULT_PERSISTENT_STATE: Record<PersistentSectionId, boolean> = {
  object: false,
  layout: false,
  effects: false,
}

const DEFAULT_SELECTION_SCOPED_STATE: Record<SelectionScopedSectionId, boolean> = {
  type: false,
  photo: false,
}

function isPersistentSectionId(id: PropertiesPanelSectionId): id is PersistentSectionId {
  return (PERSISTENT_SECTION_IDS as readonly string[]).includes(id)
}

const PINNABLE_SECTION_IDS = ['layout', 'effects'] as const

type PinnableSectionId = (typeof PINNABLE_SECTION_IDS)[number]

function isPinnableSectionId(id: PropertiesPanelSectionId): id is PinnableSectionId {
  return (PINNABLE_SECTION_IDS as readonly string[]).includes(id)
}

export interface UsePropertiesPanelSectionsResult {
  /** Ids of every currently-open section, in panel order. Pass directly as a `type="multiple"` Accordion's `value`. */
  openSectionIds: PropertiesPanelSectionId[]
  isSectionOpen: (id: PropertiesPanelSectionId) => boolean
  /** Opens a section without affecting any other section's open/closed state. */
  openSection: (id: PropertiesPanelSectionId) => void
  /** Closes a section without affecting any other section's open/closed state. */
  closeSection: (id: PropertiesPanelSectionId) => void
  /** Reconciles an Accordion `multiple` `onValueChange` array back into per-section state. */
  setOpenSectionIds: (ids: readonly string[]) => void
  /** Closes every section and clears all open-state memory (Object/Layout/Effects included) —
   *  the panel's "Collapse All" action. Never touches selection. */
  collapseAll: () => void
  /** Whichever of Layout/Effects was opened more recently, while it's still open; `null` when
   *  neither is open, or once that section is closed again (the pin is released, not handed to
   *  the other one — see this hook's doc comment). Never anything but 'layout'/'effects'/null. */
  pinnedSectionId: PropertiesPanelSectionId | null
}

export function usePropertiesPanelSections(): UsePropertiesPanelSectionsResult {
  const [persistentState, setPersistentState] = useState(DEFAULT_PERSISTENT_STATE)
  const [selectionScopedState, setSelectionScopedState] = useState(DEFAULT_SELECTION_SCOPED_STATE)
  const [pinnedSectionId, setPinnedSectionId] = useState<PinnableSectionId | null>(null)

  // Mirrors `persistentState`, always accurate as of the *previous* commit — read (never written)
  // by `setSectionOpen`/`setOpenSectionIds` to detect a genuine Layout/Effects open/close
  // transition before calling `setPinnedSectionId`, instead of comparing inside a `setPersistentState`
  // updater (which React may invoke more than once per commit).
  const persistentStateRef = useRef(persistentState)

  persistentStateRef.current = persistentState

  const applyPinTransition = useCallback((id: PinnableSectionId, open: boolean) => {
    setPinnedSectionId((prevPin) => (open ? id : prevPin === id ? null : prevPin))
  }, [])

  const setSectionOpen = useCallback(
    (id: PropertiesPanelSectionId, open: boolean) => {
      if (isPersistentSectionId(id)) {
        if (isPinnableSectionId(id) && persistentStateRef.current[id] !== open) {
          applyPinTransition(id, open)
        }

        setPersistentState((prev) => (prev[id] === open ? prev : { ...prev, [id]: open }))
      } else {
        setSelectionScopedState((prev) => (prev[id] === open ? prev : { ...prev, [id]: open }))
      }
    },
    [applyPinTransition]
  )

  const openSection = useCallback(
    (id: PropertiesPanelSectionId) => setSectionOpen(id, true),
    [setSectionOpen]
  )

  const closeSection = useCallback(
    (id: PropertiesPanelSectionId) => setSectionOpen(id, false),
    [setSectionOpen]
  )

  const setOpenSectionIds = useCallback(
    (ids: readonly string[]) => {
      const nextOpen = new Set(ids)

      for (const id of PINNABLE_SECTION_IDS) {
        const open = nextOpen.has(id)

        if (persistentStateRef.current[id] !== open) {
          applyPinTransition(id, open)
        }
      }

      setPersistentState((prev) => {
        let changed = false
        const next = { ...prev }

        for (const id of PERSISTENT_SECTION_IDS) {
          const open = nextOpen.has(id)

          if (next[id] !== open) {
            next[id] = open
            changed = true
          }
        }

        return changed ? next : prev
      })

      setSelectionScopedState((prev) => {
        let changed = false
        const next = { ...prev }

        for (const id of SELECTION_SCOPED_SECTION_IDS) {
          const open = nextOpen.has(id)

          if (next[id] !== open) {
            next[id] = open
            changed = true
          }
        }

        return changed ? next : prev
      })
    },
    [applyPinTransition]
  )

  const collapseAll = useCallback(() => {
    setPersistentState(DEFAULT_PERSISTENT_STATE)
    setSelectionScopedState(DEFAULT_SELECTION_SCOPED_STATE)
    setPinnedSectionId(null)
  }, [])

  const isSectionOpen = useCallback(
    (id: PropertiesPanelSectionId) =>
      isPersistentSectionId(id) ? persistentState[id] : selectionScopedState[id],
    [persistentState, selectionScopedState]
  )

  const openSectionIds = useMemo(
    () =>
      PROPERTIES_PANEL_SECTION_ORDER.filter((id) =>
        isPersistentSectionId(id) ? persistentState[id] : selectionScopedState[id]
      ),
    [persistentState, selectionScopedState]
  )

  return {
    openSectionIds,
    isSectionOpen,
    openSection,
    closeSection,
    setOpenSectionIds,
    collapseAll,
    pinnedSectionId,
  }
}
